import { useCallback, useEffect, useRef } from 'react'
import type { User } from 'firebase/auth'
import { doc } from 'firebase/firestore'
import type { DocumentData, DocumentReference } from 'firebase/firestore'
import { setDoc, updateDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreDoc } from './useFirestore'
import { generateCalendarToken } from '../lib/calendarToken'
import { getCalendarFeedUrl } from '../lib/utils'
import { DEFAULT_INCLUDE } from '@shared/calendar'
import type { CalendarInclude } from '@shared/calendar'
export type { CalendarInclude }
export { DEFAULT_INCLUDE }

interface CalendarDoc extends DocumentData {
  token?: string
  include?: CalendarInclude
  synced?: boolean
}

export function useCalendarToken(user: User | null) {
  const isReal = !!user && !user.isAnonymous
  const ref = isReal
    ? (doc(db, 'users', user.uid, 'private', 'calendar') as DocumentReference<CalendarDoc>)
    : null
  const { data, loading } = useFirestoreDoc<CalendarDoc>(ref)
  const writing = useRef(false)

  useEffect(() => {
    if (!isReal || loading || writing.current) return
    if (data?.token) return
    writing.current = true
    setDoc(doc(db, 'users', user!.uid, 'private', 'calendar'), {
      token: generateCalendarToken(),
      include: DEFAULT_INCLUDE,
    }).catch(() => {
      writing.current = false
    })
  }, [isReal, loading, data, user])

  const include: CalendarInclude = data?.include ?? DEFAULT_INCLUDE

  const updateInclude = useCallback(
    async (patch: Partial<CalendarInclude>) => {
      if (!ref) return
      const merged = { ...include, ...patch }
      await updateDoc(ref as DocumentReference<DocumentData>, { include: merged })
    },
    [ref, include],
  )

  const token = data?.token ?? null
  return {
    token,
    url: token ? getCalendarFeedUrl(token) : null,
    loading: isReal ? loading || !token : false,
    synced: data?.synced ?? false,
    include,
    updateInclude,
  }
}
